import { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../utils/api';
import { Button, Badge, Modal, Alert, SearchBar, Spinner, InfoRow, Pagination } from './shared';

const formatSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes >= 1048576) return (bytes / 1048576).toFixed(1) + ' MB';
    if (bytes >= 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return bytes + ' B';
};

const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('vi-VN') + ' ' + d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
};

const TYPE_OPTIONS = [
    { value: '', label: 'All Types' },
    { value: 'chart', label: 'Chart' },
    { value: 'image', label: 'Image' },
    { value: 'document', label: 'Document' },
];

const TYPE_COLORS = {
    chart: 'info',
    image: 'success',
    document: 'warning',
};

const DEFAULT_LIMIT = 50;

export default function AdminArtifacts() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [userIdFilter, setUserIdFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');
    const [includeDeleted, setIncludeDeleted] = useState(false);
    const [offset, setOffset] = useState(0);
    const [limit, setLimit] = useState(DEFAULT_LIMIT);

    const loadData = useCallback(async (newOffset) => {
        const currentOffset = newOffset !== undefined ? newOffset : offset;
        setLoading(true);
        setError(null);
        try {
            const params = { limit, offset: currentOffset };
            if (userIdFilter.trim()) params.user_id = userIdFilter.trim();
            if (typeFilter) params.artifact_type = typeFilter;
            if (includeDeleted) params.include_deleted = true;
            const res = await adminApi.listArtifacts(params);
            setItems(res.items || []);
            setTotal(res.total || 0);
        } catch (err) {
            setError(err.message || 'Failed to load artifacts');
        } finally {
            setLoading(false);
        }
    }, [userIdFilter, typeFilter, includeDeleted, offset, limit]);

    useEffect(() => { loadData(0); setOffset(0); }, [typeFilter, includeDeleted, limit]);
    useEffect(() => { loadData(); }, [offset]);

    const handleSearch = () => { setOffset(0); loadData(0); };

    const handlePageChange = (newOffset) => { setOffset(newOffset); };

    const handleLimitChange = (newLimit) => { setLimit(newLimit); setOffset(0); };

    const handleViewItem = async (id) => {
        try {
            const item = await adminApi.getArtifact(id);
            setSelectedItem(item);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDelete = async (id) => {
        if (!confirm(`Delete artifact "${id}"?`)) return;
        try {
            await adminApi.deleteArtifact(id);
            setSuccessMessage(`Deleted "${id}"`);
            setSelectedItem(null);
            setItems(prev => prev.map(i => i.id === id ? { ...i, is_deleted: true } : i));
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            setError(err.message);
        }
    };

    const selectCls = "h-9 px-3 pr-8 rounded-lg border border-gray-200 bg-white text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%239ca3af%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:12px] bg-[right_8px_center] bg-no-repeat";

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900">Artifacts</h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        {total} artifact{total !== 1 ? 's' : ''} — charts, images, documents
                    </p>
                </div>
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}
            {successMessage && <Alert variant="success" onClose={() => setSuccessMessage(null)} className="mb-4">{successMessage}</Alert>}

            <div className="flex flex-wrap items-center gap-2 mb-4">
                <SearchBar
                    value={userIdFilter}
                    onChange={setUserIdFilter}
                    onSearch={handleSearch}
                    onRefresh={() => loadData(offset)}
                    placeholder="User ID..."
                    className="w-40"
                />
                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className={selectCls}>
                    {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={includeDeleted}
                        onChange={(e) => setIncludeDeleted(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500/20"
                    />
                    Include deleted
                </label>
            </div>

            {loading ? (
                <Spinner size="lg" text="Loading..." center />
            ) : items.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-sm">No artifacts found</div>
            ) : (
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50/60">
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">ID</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 w-20">Type</th>
                                <th className="text-right px-3 py-2.5 text-xs font-medium text-gray-500 w-20">Size</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 w-20">User</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 w-24">Source</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 w-24">Tags</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 w-32">Created</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 w-24">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {items.map((item) => (
                                <tr
                                    key={item.id}
                                    className={`hover:bg-gray-50 transition-colors cursor-pointer ${item.is_deleted ? 'opacity-50' : ''}`}
                                    onClick={() => handleViewItem(item.id)}
                                >
                                    <td className="px-4 py-2.5">
                                        <span className="font-mono text-xs font-medium text-gray-900 truncate block max-w-[200px]">
                                            {item.id}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <Badge variant={TYPE_COLORS[item.type] || 'outline'}>{item.type}</Badge>
                                    </td>
                                    <td className="px-3 py-2.5 text-right">
                                        <span className="text-xs text-gray-500 font-mono">{formatSize(item.data_size)}</span>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        {item.user_id
                                            ? <Badge variant="info">{item.user_id}</Badge>
                                            : <span className="text-xs text-gray-400">-</span>
                                        }
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <span className="text-xs text-gray-500">{item.source_tool || item.source || '-'}</span>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        {item.tags && item.tags.length > 0 ? (
                                            <div className="flex gap-1 flex-wrap">
                                                {item.tags.slice(0, 2).map(t => (
                                                    <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>
                                                ))}
                                                {item.tags.length > 2 && (
                                                    <span className="text-[10px] text-gray-400">+{item.tags.length - 2}</span>
                                                )}
                                            </div>
                                        ) : (
                                            <span className="text-xs text-gray-400">-</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <span className="text-xs text-gray-500">{formatDate(item.created_at)}</span>
                                    </td>
                                    <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                                        {!item.is_deleted && (
                                            <Button
                                                variant="ghost" size="xs"
                                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                onClick={() => handleDelete(item.id)}
                                            >
                                                Delete
                                            </Button>
                                        )}
                                        {item.is_deleted && (
                                            <span className="text-[10px] text-red-400">Deleted</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="px-4 pb-3">
                        <Pagination total={total} limit={limit} offset={offset} onChange={handlePageChange} onLimitChange={handleLimitChange} />
                    </div>
                </div>
            )}

            {selectedItem && (
                <ArtifactDetailModal
                    item={selectedItem}
                    onClose={() => setSelectedItem(null)}
                    onDelete={() => handleDelete(selectedItem.id)}
                />
            )}
        </div>
    );
}

function ArtifactDetailModal({ item, onClose, onDelete }) {
    return (
        <Modal open onClose={onClose} title={`Artifact: ${item.id}`} size="xl">
            <Modal.Body>
                <div className="grid grid-cols-2 gap-x-8 gap-y-0">
                    <InfoRow label="ID" value={item.id} mono />
                    <InfoRow label="Type" value={item.type} />
                    <InfoRow label="Version" value={item.version} />
                    <InfoRow label="Size" value={formatSize(item.data_size)} />
                    <InfoRow label="Compressed" value={item.is_compressed ? 'Yes' : 'No'} />
                    <InfoRow label="User ID" value={item.user_id || '-'} />
                    <InfoRow label="Session" value={item.session_id || '-'} mono />
                    <InfoRow label="Checkpoint" value={item.checkpoint_id || '-'} mono />
                    <InfoRow label="Source" value={item.source || '-'} />
                    <InfoRow label="Source Tool" value={item.source_tool || '-'} />
                    <InfoRow label="Access Count" value={item.access_count} />
                    <InfoRow label="Checksum" value={item.checksum || '-'} mono />
                    <InfoRow label="Created" value={formatDate(item.created_at)} />
                    <InfoRow label="Updated" value={formatDate(item.updated_at)} />
                    <InfoRow label="Accessed" value={formatDate(item.accessed_at)} />
                    <InfoRow label="Expires" value={formatDate(item.expires_at)} />
                </div>

                {item.tags && item.tags.length > 0 && (
                    <div className="mt-3 flex gap-1.5 flex-wrap">
                        {item.tags.map(t => <Badge key={t} variant="outline">{t}</Badge>)}
                    </div>
                )}

                {item.metadata && Object.keys(item.metadata).length > 0 && (
                    <div className="mt-4">
                        <p className="text-sm font-medium text-gray-700 mb-2">Metadata</p>
                        <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs font-mono text-gray-800 whitespace-pre-wrap overflow-y-auto max-h-[30vh]">
                            {JSON.stringify(item.metadata, null, 2)}
                        </pre>
                    </div>
                )}

                {item.data && (
                    <div className="mt-4">
                        <p className="text-sm font-medium text-gray-700 mb-2">Data</p>
                        <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs font-mono text-gray-800 whitespace-pre-wrap overflow-y-auto max-h-[40vh]">
                            {JSON.stringify(item.data, null, 2)}
                        </pre>
                    </div>
                )}

                {item.is_compressed && !item.data && (
                    <div className="mt-4 text-center py-6 text-gray-400 text-sm bg-gray-50 rounded-lg border border-gray-200">
                        Data is compressed — view via API
                    </div>
                )}

                {!item.is_deleted && (
                    <div className="mt-4 flex justify-end">
                        <Button
                            variant="ghost" size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={onDelete}
                        >
                            Delete Artifact
                        </Button>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
}
